import {useCallback,useEffect,useState} from 'react'
import {AlertTriangle,MapPin,CheckCircle,RefreshCw,PhoneCall,X} from 'lucide-react'
import {useAuth} from '../contexts/AuthContext'
import {FamilyContext} from '../contexts/FamilyContext'
import {useContext} from 'react'
import {domusApi} from '../services/domusApi'
const locate=()=>new Promise((resolve,reject)=>{
 if(!navigator.geolocation)return reject(Error('Este dispositivo não disponibiliza geolocalização'))
 navigator.geolocation.getCurrentPosition(
  p=>resolve({lat:p.coords.latitude,lng:p.coords.longitude,accuracy:p.coords.accuracy}),
  e=>reject(Error(e.code===1?'Permissão de localização recusada':'Não foi possível obter localização atual')),
  {enableHighAccuracy:true,timeout:15000,maximumAge:0}
 )
})
export default function FamilySOS(){
 const {user}=useAuth()
 const {family}=useContext(FamilyContext)
 const [open,setOpen]=useState(false)
 const [alerts,setAlerts]=useState([])
 const [loading,setLoading]=useState(false)
 const [error,setError]=useState('')
 const [message,setMessage]=useState('Preciso de ajuda')
 const [sendGps,setSendGps]=useState(false)
 const refresh=useCallback(async()=>{
  if(!user||!family?.id){setAlerts([]);return}
  try{const result=await domusApi.sosList(family.id);setAlerts(result.data||[])}
  catch(e){setError(e.message||'Falha ao consultar SOS')}
 },[user?.uid,family?.id])
 useEffect(()=>{refresh()},[refresh])
 useEffect(()=>{
  if(!user||!family?.id)return
  const interval=setInterval(refresh,20000)
  return()=>clearInterval(interval)
 },[refresh,user?.uid,family?.id])
 const send=async()=>{
  if(!user||!family?.id)return
  if(!window.confirm('Enviar pedido de ajuda aos membros da sua família?'))return
  setLoading(true);setError('')
  try{
   let location=null
   if(sendGps){
    try{location=await locate()}
    catch(e){
     if(!window.confirm(e.message+'. Enviar SOS sem a localização?'))return
    }
   }
   await domusApi.sosSend(family.id,message,location)
   setMessage('Preciso de ajuda');setSendGps(false)
   await refresh()
  }catch(e){setError(e.message||'Não foi possível enviar SOS')}finally{setLoading(false)}
 }
 const change=async(id,action)=>{
  setLoading(true);setError('')
  try{
   if(action==='ack')await domusApi.sosAck(family.id,id)
   else await domusApi.sosResolve(family.id,id)
   await refresh()
  }catch(e){setError(e.message||'Não foi possível atualizar alerta')}finally{setLoading(false)}
 }
 const active=alerts.filter(a=>a.status==='active')
 const formatTime=date=>date?new Date(date).toLocaleString('pt-PT'):'—'
 return <div className="relative">
  <button type="button" onClick={()=>{setOpen(v=>!v);if(!open)refresh()}} className="relative flex items-center gap-1 rounded-lg bg-red-600 text-white px-3 py-2 font-bold hover:bg-red-700" aria-label="Abrir centro de SOS familiar">
    <AlertTriangle size={20}/> SOS {active.length>0&&<span className="rounded-full bg-white text-red-700 px-2 text-xs">{active.length}</span>}
  </button>
  {open&&<div role="dialog" aria-label="Centro de segurança da família" className="absolute right-0 top-full mt-2 w-[min(92vw,440px)] max-h-[78vh] overflow-y-auto rounded-xl border border-red-200 bg-white p-4 shadow-xl z-50 space-y-4">
    <div className="flex items-center justify-between"><h2 className="font-bold text-red-800">Segurança da Família</h2><button onClick={()=>setOpen(false)} aria-label="Fechar"><X size={20}/></button></div>
    <p className="text-xs text-gray-700">SOS do Domus não substitui os serviços de emergência. Em perigo imediato, telefone 112 (Portugal/UE) ou para o número de emergência local. Os avisos só aparecem a quem tiver o Domus aberto.</p>
    {!user||!family?.id ? <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">Modo de pré-visualização. Para enviar ou receber SOS reais, é necessário entrar numa família autenticada.</div>:
    <>
      <label className="block text-sm font-medium">Mensagem de ajuda<input className="w-full border rounded-lg p-2 mt-1" value={message} maxLength={280} onChange={e=>setMessage(e.target.value)}/></label>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={sendGps} onChange={e=>setSendGps(e.target.checked)}/> <MapPin size={16}/> Partilhar a localização atual neste SOS</label>
      <button disabled={loading} onClick={send} className="bg-red-600 hover:bg-red-700 text-white font-bold p-3 w-full rounded-lg disabled:opacity-50">Enviar SOS à família</button>
      <div className="flex items-center justify-between"><strong className="text-sm">Alertas familiares</strong><button onClick={refresh} disabled={loading} className="text-blue-700 flex items-center gap-1 text-sm"><RefreshCw size={14}/> Atualizar</button></div>
      {active.length===0&&<p className="text-sm text-gray-600">Nenhum SOS ativo. A aplicação consulta alertas enquanto estiver aberta.</p>}
      {active.map(a=><section key={a.id} className="border border-red-300 rounded-lg bg-red-50 p-3 space-y-2" role="alert">
       <p className="font-bold text-red-900">{a.sender_name||'Familiar'} precisa de ajuda</p><p className="text-sm">{a.message}</p>
       <p className="text-xs">Enviado: {formatTime(a.created_at)} · Confirmações: {a.acknowledgments}</p>
       {a.latitude!=null&&a.longitude!=null&&<div className="text-xs"><a className="text-blue-800 underline" href={`https://www.google.com/maps?q=${encodeURIComponent(a.latitude+','+a.longitude)}`} target="_blank" rel="noopener noreferrer">Abrir posição partilhada no mapa</a><p>Precisão aproximada: {Math.round(a.accuracy_m)} m · GPS obtido: {formatTime(a.position_time)}</p></div>}
       {a.sender_id===user.id?<button disabled={loading} onClick={()=>change(a.id,'resolve')} className="text-sm rounded-lg bg-white border p-2 text-red-900">Encerrar o meu SOS</button>:<button disabled={loading||a.acknowledged} onClick={()=>change(a.id,'ack')} className="text-sm flex items-center gap-1 rounded-lg bg-white border p-2 text-red-900"><CheckCircle size={16}/>{a.acknowledged?'Receção confirmada':'Confirmar que recebi'}</button>}
      </section>)}
    </>}
    {error&&<p role="alert" className="text-red-700 text-sm">{error}</p>}
  </div>}
 </div>
}
